import { useState, useEffect } from 'react';
import { getSegmentationAnalytics } from '../../services/segmentationService';
import BarChart from '../charts/BarChart';
import PieChart from '../charts/PieChart';

const SegmentationTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [segmentationData, setSegmentationData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getSegmentationAnalytics(30);
        setSegmentationData(data);
      } catch (error) {
        console.error('Error loading segmentation data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading segmentation analytics...</p>
      </div>
    );
  }

  if (!segmentationData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No segmentation data available</p>
      </div>
    );
  }

  // Segment distribution
  const segmentDistributionData = {
    labels: segmentationData.segments.map((s: any) => s.segmentName),
    datasets: [
      {
        label: 'Users',
        data: segmentationData.segments.map((s: any) => s.count),
        backgroundColor: ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#ef4444'],
      },
    ],
  };

  // Retention by segment
  const retentionData = {
    labels: Array.from(segmentationData.segmentComparison.retention.keys()) as string[],
    datasets: [
      {
        label: 'Retention Rate (%)',
        data: Array.from(segmentationData.segmentComparison.retention.values()) as number[],
        backgroundColor: '#10b981',
      },
    ],
  };

  // Revenue by segment
  const revenueData = {
    labels: Array.from(segmentationData.segmentComparison.revenue.keys()) as string[],
    datasets: [
      {
        label: 'Revenue',
        data: Array.from(segmentationData.segmentComparison.revenue.values()) as number[],
        backgroundColor: '#f59e0b',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        User Segmentation & Personas
      </h1>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>User Segment Distribution</h3>
          <PieChart data={segmentDistributionData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Retention by Segment</h3>
          <BarChart data={retentionData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Revenue by Segment</h3>
          <BarChart data={revenueData} height={300} />
        </div>
      </div>

      {/* Segment Details Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Segment Characteristics
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Segment</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Users</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Percentage</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Retention Rate</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Revenue</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Avg Sessions/Week</th>
            </tr>
          </thead>
          <tbody>
            {segmentationData.segments.map((segment: any, index: number) => (
              <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '1rem' }}>{segment.segmentName}</td>
                <td style={{ padding: '1rem' }}>{segment.count}</td>
                <td style={{ padding: '1rem' }}>{segment.percentage.toFixed(1)}%</td>
                <td style={{ padding: '1rem', color: segment.retentionRate > 50 ? '#10b981' : '#ef4444' }}>
                  {segment.retentionRate.toFixed(1)}%
                </td>
                <td style={{ padding: '1rem' }}>${segment.revenue.toFixed(2)}</td>
                <td style={{ padding: '1rem' }}>{segment.avgSessionsPerWeek.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SegmentationTab;

